import * as React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPlay,
  faPause,
  IconDefinition,
  faForwardStep,
} from "@fortawesome/free-solid-svg-icons"
import { areTrackInfosSame, togglePlayPause } from "./buttonUtils"
import styled from "styled-components"
import AudioPlayerContext, { TrackInfo } from "../../context/AudioPlayerContext"

interface ButtonProps {
  small?: boolean
  white?: boolean
  onClick: () => void
  icon: IconDefinition
  disabled?: boolean
}

interface ConfiguredButton {
  small?: boolean
  white?: boolean
}

const ButtonEl = styled.button<{
  small?: boolean
  white?: boolean
}>`
  background: transparent;
  padding: 0;
  font-size: ${props => (props.small ? "15px" : "3.05rem")};
  cursor: pointer;
  border-radius: 50%;
  border: ${props =>
    props.small
      ? "none"
      : `0.28rem solid ${props.white ? "white" : "currentColor"}`};
  width: ${props => (props.small ? "1.8rem" : "5.8rem")};
  height: ${props => (props.small ? "1.8rem" : "5.8rem")};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => (props.white ? "white" : "var(--color-link)")};
  flex: 0 0 auto;

  svg {
    margin-left: 0.08em;
  }

  &:disabled {
    cursor: default;
  }

  @media (max-width: 42rem) {
    width: ${props => (props.small ? "1.8rem" : "4.35rem")};
    height: ${props => (props.small ? "1.8rem" : "4.35rem")};
    font-size: ${props => (props.small ? "15px" : "2.25rem")};
    border-width: ${props => (props.small ? "0" : "0.22rem")};
  }
`

const Button: React.FC<ButtonProps> = ({
  small,
  white,
  onClick,
  icon,
  disabled,
}) => {
  return (
    <ButtonEl small={small} white={white} disabled={disabled} onClick={onClick}>
      <FontAwesomeIcon
        opacity={disabled ? 0.5 : 1}
        fontSize={small ? "15px" : ""}
        icon={icon}
      />
    </ButtonEl>
  )
}

export const ForwardButton: React.FC<ConfiguredButton> = ({ small, white }) => {
  const audioPlayerContext = React.useContext(AudioPlayerContext)

  if (!audioPlayerContext) {
    return null
  }

  const {
    switchToTrack,
    currentTrack,
    trackInfo: ctxTrackInfo,
  } = audioPlayerContext

  const [isActive, setIsActive] = React.useState(true)

  React.useEffect(() => {
    if (currentTrack && ctxTrackInfo) {
      const i = ctxTrackInfo.trackList.findIndex(
        t => t.title === currentTrack.title
      )
      setIsActive(i < ctxTrackInfo.trackList.length - 1)
    }
  }, [currentTrack])

  const next = () => {
    if (currentTrack && ctxTrackInfo) {
      const i = ctxTrackInfo.trackList.findIndex(
        t => t.title === currentTrack.title
      )
      if (i < ctxTrackInfo.trackList.length - 1) {
        switchToTrack(i + 1)
      }
    }
  }

  return (
    <Button
      small={small}
      white={white}
      onClick={next}
      disabled={!isActive}
      icon={faForwardStep}
    />
  )
}

const PlayPauseButton: React.FC<
  ConfiguredButton & {
    trackInfo: TrackInfo
  }
> = ({ small, white, trackInfo }) => {
  const audioPlayerContext = React.useContext(AudioPlayerContext)

  if (!audioPlayerContext) {
    return null
  }

  const {
    audioRef,
    isPlaying,
    setIsPlaying,
    trackInfo: ctxTrackInfo,
    setTrackInfo,
    setShouldAutoPlay,
  } = audioPlayerContext

  const [isCurrentTrackInfoLoaded, setIsCurrentTrackInfoLoaded] =
    React.useState(false)

  React.useEffect(() => {
    setIsCurrentTrackInfoLoaded(areTrackInfosSame(ctxTrackInfo, trackInfo))
  }, [ctxTrackInfo, trackInfo])

  // Check if the currently playing MP3 is the same as the one for this button
  const click = () => {
    // If we're not on the current page's MP3, load it and play
    if (!ctxTrackInfo || ctxTrackInfo.src !== trackInfo.src) {
      setTrackInfo(trackInfo)
      // Set shouldAutoPlay to true so the context will auto-play when the trackInfo is set
      setShouldAutoPlay(true)
      // We need to wait for the track info to be set before playing
      // This will be handled in the AudioPlayerContext useEffect
      return
    }
    // If we're on the same MP3, toggle play/pause normally
    togglePlayPause(isPlaying, audioRef.current, setIsPlaying)
  }

  // Show play button if:
  // 1. No track is loaded yet, OR
  // 2. The currently playing MP3 is not the one for this page
  // Otherwise, show the appropriate play/pause button based on isPlaying state
  const showPlayButton =
    !ctxTrackInfo || ctxTrackInfo.src !== trackInfo.src || !isPlaying

  return (
    <Button
      small={small}
      white={white}
      onClick={click}
      icon={showPlayButton ? faPlay : faPause}
    />
  )
}

export { PlayPauseButton }
